import { Link, useParams } from 'react-router-dom';
import { useCurrentUser } from '../account/useAuth';
import { AppFooter } from '../branding/AppFooter';
import { AppHeader } from '../branding/AppHeader';
import { GalleryCoverArt } from '../gallery/GalleryCoverArt';
import { useProfile } from './useProfile';
import type { ProfilePublishedApp, ProfileSubmission } from './profileApi';
import './ProfilePage.css';

/**
 * `/u/<username>` — a kid's public profile.
 *
 * Same two-lane structure as /gallery (Remix kits + Live apps) but
 * scoped to one creator. Visiting this URL is the canonical share
 * path for a kid's body of work; later slices add a vanity
 * `<username>.brytaapps.com` redirect that resolves here.
 */
export function ProfilePage(): JSX.Element {
  const { username = '' } = useParams<{ username: string }>();
  const { data: profile, isPending, error } = useProfile(username);
  const { data: me } = useCurrentUser();
  const isMine = !!me?.username && me.username.toLowerCase() === username.toLowerCase();

  if (isPending) {
    return (
      <PageShell>
        <p className="bl-profile__notice" aria-live="polite">
          Loading…
        </p>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <h1 className="bl-profile__title">Hmm.</h1>
        <p className="bl-profile__notice bl-profile__notice--error" role="alert">
          {error.message}
        </p>
        <p>
          <Link to="/gallery">← Back to the gallery</Link>
        </p>
      </PageShell>
    );
  }

  if (!profile) {
    return (
      <PageShell>
        <p className="bl-profile__notice">No data.</p>
      </PageShell>
    );
  }

  const handle = '@' + profile.username;
  const heading = profile.display_name || handle;
  const empty = profile.submissions.length === 0 && profile.published_apps.length === 0;

  return (
    <PageShell>
      <header className="bl-profile__hero">
        <div className="bl-profile__avatar" aria-hidden="true">
          {profile.username.charAt(0).toUpperCase()}
        </div>
        <div>
          <h1 className="bl-profile__title">{heading}</h1>
          {profile.display_name && <p className="bl-profile__handle">{handle}</p>}
          <p className="bl-profile__counts">
            {profile.total_submissions.toLocaleString()} remix kit
            {profile.total_submissions === 1 ? '' : 's'} ·{' '}
            {profile.total_published_apps.toLocaleString()} live app
            {profile.total_published_apps === 1 ? '' : 's'}
          </p>
          {isMine && (
            <p className="bl-profile__youtag">
              This is your profile. Share <code>/u/{profile.username}</code> with anyone.
            </p>
          )}
        </div>
      </header>

      {empty ? (
        <section className="bl-profile__empty" aria-live="polite">
          <h2>No public work yet.</h2>
          <p>
            Once {profile.display_name || handle}&rsquo;s submissions are approved or apps shipped,
            they&rsquo;ll show up here.
          </p>
        </section>
      ) : (
        <>
          {profile.submissions.length > 0 && (
            <section className="bl-profile__section" aria-labelledby="bl-profile-remix-heading">
              <h2 id="bl-profile-remix-heading">
                Remix kits
                <span className="bl-profile__count">
                  {profile.total_submissions.toLocaleString()}
                </span>
              </h2>
              <ul className="bl-profile__grid">
                {profile.submissions.map((s) => (
                  <SubmissionTile key={s.id} entry={s} />
                ))}
              </ul>
            </section>
          )}
          {profile.published_apps.length > 0 && (
            <section className="bl-profile__section" aria-labelledby="bl-profile-apps-heading">
              <h2 id="bl-profile-apps-heading">
                Live apps
                <span className="bl-profile__count">
                  {profile.total_published_apps.toLocaleString()}
                </span>
              </h2>
              <ul className="bl-profile__grid bl-profile__grid--apps">
                {profile.published_apps.map((a) => (
                  <AppTile key={a.id} app={a} />
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </PageShell>
  );
}

function PageShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="bl-page">
      <AppHeader privacy="published" back={{ label: 'Back to gallery', to: '/gallery' }} />
      <main className="bl-profile">{children}</main>
      <AppFooter />
    </div>
  );
}

function SubmissionTile({ entry }: { entry: ProfileSubmission }): JSX.Element {
  return (
    <li className="bl-profile__card">
      <Link
        to={`/gallery/${entry.id}`}
        className="bl-profile__card-cover"
        aria-label={`Open ${entry.project_name}`}
      >
        <GalleryCoverArt
          name={entry.project_name}
          kind={entry.project_kind}
          labels={entry.class_labels}
        />
      </Link>
      <div className="bl-profile__card-body">
        <Link to={`/gallery/${entry.id}`} className="bl-profile__card-name">
          {entry.project_name}
        </Link>
        <p className="bl-profile__card-kind">{entry.project_kind} · model</p>
        {entry.description && <p className="bl-profile__card-desc">{entry.description}</p>}
        <p className="bl-profile__card-meta">
          {entry.class_labels.length > 0 ? entry.class_labels.slice(0, 4).join(' · ') : 'No labels'}
        </p>
      </div>
    </li>
  );
}

function AppTile({ app }: { app: ProfilePublishedApp }): JSX.Element {
  return (
    <li className="bl-profile__card bl-profile__card--app">
      <a
        href={app.share_url}
        target="_blank"
        rel="noopener noreferrer"
        className="bl-profile__card-cover"
        aria-label={`Open ${app.title} in a new tab`}
      >
        <GalleryCoverArt name={app.title} kind="app" labels={[]} />
      </a>
      <div className="bl-profile__card-body">
        <a
          href={app.share_url}
          target="_blank"
          rel="noopener noreferrer"
          className="bl-profile__card-name"
        >
          {app.title} <span aria-hidden="true">↗</span>
        </a>
        <p className="bl-profile__card-kind">live mini-app · v{app.version}</p>
        <p className="bl-profile__card-meta">{app.view_count.toLocaleString()} visits</p>
      </div>
    </li>
  );
}
